import { useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { isLiveObjective } from '@nabu/shared/domain';
import { localised, useI18n } from './i18n';
import { PLAN_SECTIONS } from './nav';
import { sectionHref, tabLabel } from './tabs';
import { usePlans } from './plan/Plans';
import { roomForAnother } from './plan/room';
import { NEW_PLAN, optionLabel, planChoice, planSelector } from './plan/selector';
import { ROUTES } from './routes';

/**
 * The plan selector, and the plan's own navigation under it. Both live in the fixed header.
 *
 * Leo, 2026-09-20: *"I want a plan drop down select at the top of the ui… a horizontal menu
 * below the logo and plan selector is the navigation for the plan, which has plan, progress and
 * chat as options."*
 *
 * **Two levels of navigation, and the split is scope.** *Which plan* is a control at the top;
 * *within the plan* is a bar of three under it; everything that is not about one plan is in the
 * hamburger. The isolation under all of it is `sectionHref`, unchanged, still the only thing
 * that decides which addresses carry `?o=`.
 *
 * **In the header rather than in the page**, which is the layout decision and its price is paid
 * in `styles.css`. The pill bar this replaces was the first child of `.stage`, so it scrolled
 * away with the content — acceptable for a bar that existed on three screens for a learner with
 * two plans, and not acceptable for the app's navigation: *"where am I, where else can I go"*
 * has to be where it always was, which is the argument `.topbar` already makes for itself. The
 * price is that `--bar-h` grows by a row, and `--bar-h` is what the chat computes its box from.
 * See `--plan-nav-h` in `styles.css`: a constant toggled by `body:has(.plan-nav)`, exactly as
 * `--notice-h` is, so the arithmetic stays arithmetic and nothing measures anything.
 *
 * **The two halves are two components because they are two rows.** The selector sits in
 * `.topbar-inner` beside the wordmark and the bar sits beneath it, and a single component
 * returning both would have to be rendered in one of them. They share this hook instead, which
 * is cheap: a context read and two pure functions.
 *
 * **Every decision either of them makes lives in `plan/selector.ts`**, for the reason `tabs.ts`
 * gives: `npm test` does not type-check `.tsx`, so a rule decided in here is a rule no test in
 * this repository can see.
 */
function usePlanChrome() {
  const location = useLocation();
  const { plans, defaultId, current, noteCurrent } = usePlans();

  const named = new URLSearchParams(location.search).get('o') ?? undefined;

  /*
   * The address is remembered so the global screens can keep naming the plan the learner is in.
   *
   * Configuration and the profile carry no `?o=` and must not — see `sectionHref` — so without
   * this the selector would fall back to the API's default the moment somebody opened their
   * quiet hours, which is a different plan whenever a different plan was asked from more
   * recently. See `PlansState.current`.
   */
  useEffect(() => {
    if (named) noteCurrent(named);
  }, [named, noteCurrent]);

  /* `isLiveObjective` from the shared **subpath** and not the barrel: the barrel pulls
     `node:crypto` into the browser bundle and breaks the build. */
  const live = plans?.filter((plan) => isLiveObjective(plan.status));

  return {
    live,
    objectiveId: named ?? current ?? defaultId,
    pathname: location.pathname,
    /*
     * Whether a plan is being stated, read from the address like everything else here.
     *
     * **Nothing is noted for it, and that is load-bearing.** The effect above remembers `?o=` so
     * the global screens can keep naming the plan a learner is in; this screen carries no `?o=`,
     * so `current` is left holding whichever plan they were last actually in — which is exactly
     * what the selector should offer them back, and what `planChoice` should return them to if
     * they abandon this. A sentinel written into `current` would have leaked the clean slate into
     * the header of every global screen for the rest of the session.
     */
    creating: location.pathname === ROUTES.newPlan,
  };
}

/**
 * Which plan, at the top of the app.
 *
 * `cap` is the tier's slot count off `/api/auth/me`, and it goes through `roomForAnother` —
 * the same function the plan screen's create form asks — so *add one* and the form it leads to
 * are one decision rather than two that happen to agree. It degrades towards offering when the
 * cap cannot be read, which is deliberate: see that file.
 */
export function PlanSelect({ cap }: { cap: number | undefined }) {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const { live, objectiveId, pathname, creating } = usePlanChrome();

  const selector = planSelector({
    plans: live,
    selected: objectiveId,
    offer: roomForAnother({ live: live?.length, cap }),
    creating,
    labelOf: (plan, index) =>
      optionLabel(
        tabLabel({
          /* Resolved here because `localised` needs the reader's locale and `tabs.ts` stays
             React-free so its rules can be tested. An empty string when there is no title yet,
             which is exactly what `tabLabel` treats as "no title". */
          title: plan.title ? localised(plan.title, locale, '') : '',
          description: plan.description,
          fallback: t('tabs.plan'),
          index,
        }),
        plan.unread,
      ),
  });

  if (selector.kind === 'hidden') return null;

  if (selector.kind === 'name') {
    /*
     * One plan, and nowhere else to go: its name, as text.
     *
     * Not a `<select>` with one option, which is a control that opens a list of the thing you
     * are already looking at — the dead control `objectiveTabs` refused to draw. Not nothing
     * either, because the bar below it is plan-scoped navigation and an unnamed scope is half
     * of what the owner is complaining about. `title` carries the whole string for a name CSS
     * has had to ellipsise on a narrow phone.
     */
    return (
      <span className="plan-name" title={selector.label}>
        {selector.label}
      </span>
    );
  }

  return (
    /*
     * **A native `<select>`, chosen over a listbox, and the reasons are all about the handset
     * this is answered on.** It renders as the platform's own picker — a wheel on iOS, a sheet
     * on Android — drawn *outside* the page, so a plan whose name is longer than the top bar is
     * still readable and the list cannot be clipped by a header two rows tall. It is operable
     * by keyboard and announced as a control for free: no arrow-key contract to implement, no
     * focus to manage, no `aria-expanded` to keep in step with a state. A listbox would be more
     * controllable — a pill per plan, a count drawn as an element — and every one of those
     * gains is markup we would have to get right on devices we cannot test on, for a control
     * pressed a few times a week.
     *
     * The cost is real and is paid in `optionLabel`: an `<option>` is text to the platform, so
     * a waiting count is text in parentheses rather than the amber pill the tabs drew.
     *
     * **A `<label>` and not an `aria-label`**, so the control has a name in the accessibility
     * tree that is a real element. `sr-only` on the words because the chosen plan's own name is
     * the visible label a sighted learner reads, and a header two rows tall has no room for a
     * caption above it.
     */
    <label className="plan-select-field">
      <span className="sr-only">{t('plans.label')}</span>
      <select
        className="plan-select"
        value={selector.value}
        onChange={(event) => {
          const value = event.target.value;
          /* The placeholder is not a destination — it is what the control shows when the
             address names a plan that is no longer live, and choosing it would be a navigation
             to nowhere. It is `disabled` below as well; this is the guard for a platform picker
             that lets it through anyway. */
          if (value === '') return;
          navigate(planChoice({ value, pathname }));
        }}
      >
        {selector.creating && (
          /*
           * Where the learner is: not in a plan, making one.
           *
           * **Drawn as the chosen entry rather than as a placeholder**, and not `disabled`. A
           * `<select>` must show something, and on this screen the honest something is the state
           * itself — the owner's *"the selector says where you are"*. Enabled because a native
           * picker on a phone is a sheet the learner opens to look at their plans and may well
           * close by tapping the row that was already chosen; a disabled entry makes that tap do
           * nothing at all, which reads as a stuck control. Choosing it navigates to the address
           * it is already on, which is a no-op.
           *
           * First in the list, above the plans, because it is where they are and the plans are
           * where they may go.
           */
          <option value={NEW_PLAN}>{t('plans.new')}</option>
        )}
        {selector.stray && (
          /*
           * The address names a plan that is not live — an old notification, a plan closed on
           * another device. The bar this replaces marked nothing, which a select cannot do;
           * this says the same thing in the one place a select has to show something.
           */
          <option value="" disabled>
            {t('plans.choose')}
          </option>
        )}
        {selector.options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
        {selector.addOne && <option value={NEW_PLAN}>{t('plans.add')}</option>}
      </select>
    </label>
  );
}

/**
 * The plan's own three screens, in a row under the selector.
 *
 * **Drawn on the global screens too**, which is deliberate rather than an oversight. It is what
 * keeps the conversation one tap from anywhere now that the bar's chat button is gone — a
 * learner who went to configuration to move their quiet hours gets back to the question waiting
 * for them with one press — and a bar that stays put across the whole app is also what says the
 * global screens are *outside* the plan rather than a fourth section of it.
 *
 * Links and not an ARIA `tablist`, which is `ObjectiveTabs`' argument and survives the rewrite
 * unchanged: each one is an address, it goes in the browser's history, it can be shared, and
 * middle-click works. `role="tablist"` would promise a keyboard contract this does not implement
 * and would tell a screen reader a panel is about to change when the page is about to be
 * navigated. `NavLink` marks the current entry `aria-current="page"` by itself.
 */
export function PlanNav() {
  const { t } = useI18n();
  const { objectiveId } = usePlanChrome();

  return (
    <nav className="plan-nav" aria-label={t('nav.thisPlan')}>
      <ul>
        {PLAN_SECTIONS.map((section) => (
          <li key={section.to}>
            <NavLink to={sectionHref(section.to, objectiveId)} className="plan-nav-item">
              {t(section.label)}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
